import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Shield, 
  Globe, 
  Zap, 
  Target, 
  Eye, 
  Heart,
  ArrowRight,
  Users,
  Award,
  Clock,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import CTABanner from "@/components/CTABanner";

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: "Trust & Integrity",
      description: "Building lasting relationships through honest and transparent business practices."
    },
    {
      icon: Zap,
      title: "Innovation",
      description: "Continuously pushing boundaries to deliver cutting-edge solutions."
    },
    {
      icon: Heart,
      title: "Client Focus",
      description: "Your success is our priority. We go above and beyond for every client."
    },
    {
      icon: Globe,
      title: "Global Mindset",
      description: "Serving clients worldwide with cultural awareness and local expertise."
    }
  ];

  const stats = [
    { value: "5+", label: "Years Experience" },
    { value: "100+", label: "Projects Completed" },
    { value: "50+", label: "Happy Clients" },
    { value: "24/7", label: "Support Available" }
  ];

  const milestones = [
    { year: "2019", title: "Company Founded", description: "GWIT was established with a vision to transform digital landscapes." },
    { year: "2020", title: "First Major Project", description: "Delivered enterprise solution for a Fortune 500 company." },
    { year: "2021", title: "Blockchain Division", description: "Expanded into cryptocurrency and blockchain development." },
    { year: "2022", title: "Global Expansion", description: "Opened operations to serve international clients." },
    { year: "2023", title: "100+ Projects", description: "Reached milestone of 100+ successful project deliveries." },
    { year: "2024", title: "Industry Recognition", description: "Recognized as leading IT solutions provider in Bangalore." }
  ];

  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-600/10 to-transparent" />
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-20">
          <div className="w-full h-full bg-gradient-to-bl from-blue-500/30 to-transparent blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-blue-400 mb-4 block">
              About Us
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-gradient leading-tight mb-6">
              Building Digital Excellence Since 2019
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
              Great World Infotech Pvt Ltd is a next-generation IT solutions provider committed to transforming businesses through innovative technology solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="glass rounded-2xl p-6 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <div className="text-4xl sm:text-5xl font-bold text-blue-400 mb-2">{stat.value}</div>
                <div className="text-slate-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CEO Section */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[#0F172A]/50" />
        
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative">
                <div className="w-64 h-64 mx-auto lg:mx-0 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center text-white text-6xl font-bold">
                  RR
                </div>
                <div className="absolute -bottom-4 -right-4 glass rounded-xl p-4">
                  <div className="text-blue-400 font-bold">Founder & CEO</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-blue-400 mb-4 block">
                Leadership
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Mr. Raghu R
              </h2>
              <p className="text-lg text-slate-300 mb-6">
                "Our mission is to empower businesses with technology that drives growth and innovation. At GWIT, we don't just deliver solutions – we build partnerships that last."
              </p>
              <p className="text-slate-400 mb-8">
                With over a decade of experience in the IT industry, Mr. Raghu R founded GWIT with a vision to provide world-class technology solutions to businesses of all sizes. His leadership has been instrumental in positioning GWIT as a trusted partner for digital transformation.
              </p>
              <Link to="/contact">
                <Button
                  className="bg-blue-600 text-white rounded-full px-6 py-5 font-semibold hover:bg-blue-500 transition-all group"
                  data-testid="ceo-contact-btn"
                >
                  Get in Touch
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              className="glass rounded-2xl p-8 sm:p-10"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-14 h-14 rounded-xl bg-blue-600/20 flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
              <p className="text-slate-300">
                To deliver innovative, scalable, and secure IT solutions that empower businesses to thrive in the digital age. We are committed to exceeding client expectations through technical excellence and dedicated service.
              </p>
            </motion.div>

            <motion.div
              className="glass rounded-2xl p-8 sm:p-10"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="w-14 h-14 rounded-xl bg-cyan-600/20 flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-cyan-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
              <p className="text-slate-300">
                To be the global leader in IT solutions, recognized for our innovation, reliability, and commitment to client success. We envision a future where technology seamlessly transforms businesses worldwide.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="relative py-24 sm:py-32 bg-[#0F172A]/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-blue-400 mb-4 block">
              Our Values
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              What We Stand For
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="glass rounded-2xl p-6 text-center glass-hover"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="w-14 h-14 mx-auto rounded-xl bg-blue-600/20 flex items-center justify-center mb-4">
                  <value.icon className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">{value.title}</h3>
                <p className="text-sm text-slate-400">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-blue-400 mb-4 block">
              Our Journey
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              Milestones
            </h2>
          </motion.div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500/50 via-blue-500/20 to-transparent" />

            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  className={`relative flex items-center gap-8 ${
                    index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  {/* Node */}
                  <div className="absolute left-4 md:left-1/2 w-3 h-3 -translate-x-1/2 rounded-full bg-blue-500 z-10">
                    <div className="absolute inset-0 rounded-full bg-blue-500 animate-ping opacity-50" />
                  </div>

                  {/* Content */}
                  <div className={`ml-12 md:ml-0 md:w-1/2 ${index % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12"}`}>
                    <div className="glass rounded-xl p-6 glass-hover">
                      <span className="text-blue-400 font-bold">{milestone.year}</span>
                      <h4 className="text-white font-semibold mt-1">{milestone.title}</h4>
                      <p className="text-sm text-slate-400 mt-2">{milestone.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
};

export default AboutPage;
